/**
 * Sanitization utilities for user inputs
 * Prevents XSS and injection attacks
 */

/**
 * Maximum length for string inputs
 */
const MAX_STRING_LENGTH = 1000;

/**
 * Sanitize a string input
 * - Trims whitespace
 * - Escapes HTML entities
 * - Limits length
 * @param input - String to sanitize
 * @returns Sanitized string
 */
export function sanitizeString(input: string): string {
  if (typeof input !== "string") {
    return "";
  }

  // Trim whitespace
  let sanitized = input.trim();

  // Limit length
  if (sanitized.length > MAX_STRING_LENGTH) {
    sanitized = sanitized.substring(0, MAX_STRING_LENGTH);
  }

  // Escape HTML entities to prevent XSS
  const htmlEscapes: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "/": "&#x2F;",
  };

  sanitized = sanitized.replace(/[&<>"'/]/g, (match) => htmlEscapes[match] || match);

  return sanitized;
}

/**
 * Sanitize a filter value based on its type
 * @param value - Filter value to sanitize
 * @returns Sanitized value
 */
export function sanitizeFilterValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string") {
    return sanitizeString(value);
  }

  if (typeof value === "number") {
    // Validate number is finite
    if (!Number.isFinite(value)) {
      return null;
    }
    return value;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    // Sanitize each element in the array
    return value.map((item) => {
      if (typeof item === "string") {
        return sanitizeString(item);
      }
      if (typeof item === "number") {
        return Number.isFinite(item) ? item : null;
      }
      return item;
    }).filter((item) => item !== null);
  }

  // For unknown types, return as-is (will be validated by Zod)
  return value;
}

/**
 * Sanitize a field name
 * @param fieldName - Field name to sanitize
 * @returns Sanitized field name
 */
export function sanitizeFieldName(fieldName: string): string {
  if (typeof fieldName !== "string") {
    return "";
  }

  // Trim and limit length
  let sanitized = fieldName.trim();
  if (sanitized.length > 100) {
    sanitized = sanitized.substring(0, 100);
  }

  // Remove any characters that aren't alphanumeric, underscore, or dot
  sanitized = sanitized.replace(/[^a-zA-Z0-9_.]/g, "");

  return sanitized;
}

